import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import SkeletonLoader from '../../components/SkeletonLoader';
import { useToast } from '../../contexts/ToastContext';
import { fetchBlogsByCategory, fetchBlogCategoryBySlug } from '../../api/blogApi'; // Import fetchBlogCategoryBySlug
import MetaTagsUpdater from '../../components/MetaTagsUpdater';
import NoContentMessage from '../../components/NoContentMessage';

function BlogsByCategoryPage() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryName, setCategoryName] = useState(''); // New state for category name
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { slug: categorySlug } = useParams(); // Get category slug from URL
  const location = useLocation();

  const blogsPerPage = 12;

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const fetchData = async () => {
      if (!categorySlug) {
        setLoading(false);
        setBlogPosts([]);
        addToast('error', 'Category not specified.');
        return;
      }

      setLoading(true);
      try {
        // Fetch category details
        const categoryResponse = await fetchBlogCategoryBySlug(categorySlug);
        if (categoryResponse.success && categoryResponse.data) {
          setCategoryName(categoryResponse.data.name);
        } else {
          setCategoryName(categorySlug.replace(/-/g, ' ')); // Fallback to slug if API fails
          addToast('error', categoryResponse.message || `Failed to fetch category details for "${categorySlug}".`);
        }

        // Fetch blog posts for the category
        const blogsResponse = await fetchBlogsByCategory(categorySlug, { paginate: true, per_page: blogsPerPage, page: currentPage });

        if (blogsResponse.success) {
          const fetchedBlogsData = blogsResponse.data;
          const paginationMeta = blogsResponse.pagination;

          const formattedPosts = fetchedBlogsData.map(blog => ({
            id: blog.id,
            image: blog.image,
            title: blog.title,
            date: new Date(blog.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
            description: blog.excerpt,
            slug: blog.slug,
          }));
          setBlogPosts(formattedPosts);
          setTotalPages(paginationMeta.last_page || 1);
        } else {
          addToast('error', blogsResponse.message || `Failed to fetch blog posts for category "${categorySlug}".`);
          setBlogPosts([]);
          setTotalPages(1);
        }
      } catch (e) {
        console.error("Error fetching data for category page:", e);
        addToast('error', 'Failed to load category blogs. Please try again later.');
        setBlogPosts([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [addToast, location.search, currentPage, categorySlug]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      const newQueryParams = new URLSearchParams(location.search);
      newQueryParams.set('page', pageNumber);
      navigate(`${location.pathname}?${newQueryParams.toString()}`);
    }
  };

  return (
    <div className="min-h-screen bg-bg-dark text-text-white pt-20">
      <MetaTagsUpdater pageKey="BlogCategories" dynamicTitle={categoryName ? `Blogs in ${categoryName}` : `Blogs in ${categorySlug.replace(/-/g, ' ')}`} />
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-center text-text-white mb-16 relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-accent-red after:rounded-sm after:bottom-[-15px]">
            Blogs in Category: {categoryName || (categorySlug ? categorySlug.replace(/-/g, ' ') : 'N/A')}
          </h1>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <SkeletonLoader type="blog-card" count={blogsPerPage} />
          </div>
        ) : blogPosts.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <div key={post.id} className="bg-gray-800 rounded-lg shadow-lg overflow-hidden border border-border-subtle">
                  <img src={post.image} alt={post.title} className="w-full h-48 object-cover" />
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{post.title}</h3>
                    <p className="text-text-gray text-sm mb-4">{post.date}</p>
                    <p className="text-text-gray mb-4">{post.description}</p>
                    <Link
                      to={`/blogs/${post.slug}`}
                      className="inline-block text-accent-red hover:underline mt-2"
                    >
                      Read More →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-12">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-md bg-gray-700 text-text-white hover:bg-accent-red disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => handlePageChange(i + 1)}
                    className={`px-4 py-2 rounded-md ${
                      currentPage === i + 1 ? 'bg-accent-red text-white' : 'bg-gray-700 text-text-white hover:bg-gray-600'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-md bg-gray-700 text-text-white hover:bg-accent-red disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <NoContentMessage message={`No blog posts found for category "${categoryName || (categorySlug ? categorySlug.replace(/-/g, ' ') : 'N/A')}".`} />
        )}
      </div>
    </div>
  );
}

export default BlogsByCategoryPage;